import React from 'react';
import { BreakpointsOfTOrT } from '../../theme';
import { useBreakpointMap } from '../../utils/useBreakpointMap';
import { ProgressBar } from './ProgressBar';
import { ProgressCircle } from './ProgressCircle';
import {
  ProgressStepMarker,
  ProgressStepMarkerProps,
} from './ProgressStepMarker';
import { renderStepMarker } from './renderStepMarker';
import { useInitialValue } from './utils';

function isStepMarker(
  element: ProgressProps['children'][number]
): element is ProgressProps['children'][number] {
  return element.type === ProgressStepMarker;
}

const defaultSize: BreakpointsOfTOrT<ProgressSize> = {
  xs: 'small',
  md: 'large',
};

export type ProgressSize = 'small' | 'large';

export type ProgressProps = {
  children: React.FunctionComponentElement<ProgressStepMarkerProps>[];
  currentStep: number;
  size?: BreakpointsOfTOrT<ProgressSize>;
  heading: string;
  subHeading: string;
  subHeadingTimingText?: string;
};

/**
 * Progress component requires each direct child element to be
 * a `ProgressStepMarker` component.
 * Its props are enriched to include details about the
 * current state of the progress bar.
 *
 */
export const Progress: React.FC<ProgressProps> = ({
  children,
  currentStep,
  heading,
  size = defaultSize,
  subHeading,
  subHeadingTimingText,
}) => {
  const animatedCurrentStep = useInitialValue(currentStep);

  const validChildren = children.filter(isStepMarker);
  const validStepCount = validChildren.length;

  const stepMarkers = React.Children.map(
    validChildren,
    renderStepMarker(animatedCurrentStep)
  );

  const mappedSize = useBreakpointMap(size);
  return mappedSize === 'large' ? (
    <ProgressBar
      size={mappedSize}
      heading={heading}
      subHeading={subHeading}
      currentStep={animatedCurrentStep}
      stepCount={validStepCount}
      subHeadingTimingText={subHeadingTimingText}
    >
      {stepMarkers}
    </ProgressBar>
  ) : (
    <ProgressCircle
      size={mappedSize}
      currentStep={animatedCurrentStep}
      stepCount={validStepCount}
      progressText={`${currentStep} of ${validStepCount}`}
      subHeadingTimingText={subHeadingTimingText}
    >
      {stepMarkers}
    </ProgressCircle>
  );
};
